import QRCode from "qrcode";

/**
 * Generate QR code as data URL
 */
export async function generateQRCodeDataUrl(url: string): Promise<string> {
  return await QRCode.toDataURL(url, {
    width: 512,
    margin: 2,
    color: { dark: "#000000", light: "#ffffff" },
  });
}

/**
 * Pre-load a logo image for reuse across multiple labels
 */
export async function preloadLogoImage(logoUrl: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const logoImg = new Image();
    logoImg.crossOrigin = "anonymous";
    
    const timeoutId = setTimeout(() => {
      console.log("[Label Preload] Logo load timeout");
      resolve(null);
    }, 5000);

    logoImg.onload = () => {
      clearTimeout(timeoutId);
      console.log(`[Label Preload] Logo loaded: ${logoImg.naturalWidth}x${logoImg.naturalHeight}`);
      resolve(logoImg);
    };
    logoImg.onerror = () => {
      clearTimeout(timeoutId);
      console.log("[Label Preload] Logo load failed");
      resolve(null);
    };
    logoImg.src = logoUrl;
  });
}

/**
 * Draw full printable label to canvas (4"x2" at 300 DPI = 1200x600px)
 * Avery 5163 landscape format
 * @param logoImg - Pre-loaded logo image (use preloadLogoImage) or null
 */
export async function drawLabelToCanvas(
  boxNumber: string,
  qrDataUrl: string,
  logoImg?: HTMLImageElement | null
): Promise<string> {
  const drawStart = performance.now();
  console.log("[Label Draw] Starting label creation for:", boxNumber);

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;

  // 4" x 2" at 300 DPI (landscape orientation for Avery 5163)
  const WIDTH = 1200;
  const HEIGHT = 600;
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  console.log("[Label Draw] Canvas dimensions:", WIDTH, "x", HEIGHT);

  // Background
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, WIDTH, HEIGHT);
  console.log("[Label Draw] Background filled");

  // Border (thin)
  ctx.strokeStyle = "#E5E7EB";
  ctx.lineWidth = 2;
  ctx.strokeRect(1, 1, WIDTH - 2, HEIGHT - 2);

  // Layout: QR on left, content on right
  const QR_SECTION_WIDTH = 450;
  const CONTENT_X = QR_SECTION_WIDTH + 20;
  const PADDING = 30;

  // === QR CODE (left side) ===
  console.log("[Label Draw] Loading QR image...");
  const qrLoadStart = performance.now();
  const qrImg = new Image();
  await new Promise<void>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error("QR image load timeout"));
    }, 5000);

    qrImg.onload = () => {
      clearTimeout(timeoutId);
      console.log(
        `[Label Draw] QR image loaded in ${(performance.now() - qrLoadStart).toFixed(2)}ms`
      );
      resolve();
    };
    qrImg.onerror = (err) => {
      clearTimeout(timeoutId);
      console.error("[Label Draw] QR image failed to load:", err);
      reject(err);
    };
    qrImg.src = qrDataUrl;
  });

  const qrSize = 380;
  const qrX = (QR_SECTION_WIDTH - qrSize) / 2;
  const qrY = (HEIGHT - qrSize) / 2;
  ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
  console.log("[Label Draw] QR drawn at position:", qrX, qrY, "size:", qrSize);

  // === RIGHT SIDE CONTENT ===
  let currentY = PADDING;

  // Partner logo (if pre-loaded image provided)
  console.log("[Label Draw] Logo image:", logoImg ? "present" : "none");
  if (logoImg && logoImg.complete && logoImg.naturalWidth > 0) {
    try {
      const maxLogoHeight = 50;
      const maxLogoWidth = 300;
      let logoHeight = logoImg.naturalHeight;
      let logoWidth = logoImg.naturalWidth;

      if (logoHeight > maxLogoHeight) {
        logoWidth = (logoWidth * maxLogoHeight) / logoHeight;
        logoHeight = maxLogoHeight;
      }
      if (logoWidth > maxLogoWidth) {
        logoHeight = (logoHeight * maxLogoWidth) / logoWidth;
        logoWidth = maxLogoWidth;
      }

      ctx.drawImage(logoImg, CONTENT_X, currentY, logoWidth, logoHeight);
      console.log(
        "[Label Draw] Logo drawn at:",
        CONTENT_X,
        currentY,
        "scaled to:",
        logoWidth,
        "x",
        logoHeight
      );
      currentY += logoHeight + 15;
    } catch (err) {
      console.error("[Label Draw] Logo error:", err);
      // Continue without logo
    }
  }

  // Box number
  console.log("[Label Draw] Drawing text elements...");
  ctx.font = 'bold 56px "Roboto Mono", monospace';
  ctx.fillStyle = "#111827";
  ctx.textAlign = "left";
  ctx.fillText(boxNumber, CONTENT_X, currentY + 50);
  currentY += 70;

  // Dashed divider
  ctx.setLineDash([6, 4]);
  ctx.beginPath();
  ctx.moveTo(CONTENT_X, currentY);
  ctx.lineTo(WIDTH - PADDING, currentY);
  ctx.strokeStyle = "#D1D5DB";
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.setLineDash([]);
  currentY += 25;

  // Room write-in line
  ctx.font = "500 22px Inter, sans-serif";
  ctx.fillStyle = "#374151";
  ctx.fillText("Room:", CONTENT_X, currentY + 22);

  // Underline
  ctx.beginPath();
  ctx.moveTo(CONTENT_X + 80, currentY + 25);
  ctx.lineTo(WIDTH - PADDING, currentY + 25);
  ctx.strokeStyle = "#D1D5DB";
  ctx.lineWidth = 2;
  ctx.stroke();
  currentY += 55;

  // Three-step instructions (vertical stacked list)
  const lineHeight = 35;

  // Step 1: Snap
  ctx.font = "bold 22px Inter, sans-serif";
  ctx.fillStyle = "#111827";
  ctx.textAlign = "left";
  const snap1 = "1. Snap";
  ctx.fillText(snap1, CONTENT_X, currentY);
  ctx.font = "400 22px Inter, sans-serif";
  ctx.fillStyle = "#6B7280";
  ctx.fillText(" — Photo contents before sealing", CONTENT_X + ctx.measureText(snap1).width, currentY);

  // Step 2: Scan
  currentY += lineHeight;
  ctx.font = "bold 22px Inter, sans-serif";
  ctx.fillStyle = "#111827";
  const scan2 = "2. Scan";
  ctx.fillText(scan2, CONTENT_X, currentY);
  ctx.font = "400 22px Inter, sans-serif";
  ctx.fillStyle = "#6B7280";
  ctx.fillText(" — QR code to catalog box", CONTENT_X + ctx.measureText(scan2).width, currentY);

  // Step 3: Search
  currentY += lineHeight;
  ctx.font = "bold 22px Inter, sans-serif";
  ctx.fillStyle = "#111827";
  const search3 = "3. Search";
  ctx.fillText(search3, CONTENT_X, currentY);
  ctx.font = "400 22px Inter, sans-serif";
  ctx.fillStyle = "#6B7280";
  ctx.fillText(" — Find any item instantly", CONTENT_X + ctx.measureText(search3).width, currentY);

  // Footer branding (bottom right)
  ctx.font = "400 16px Inter, sans-serif";
  ctx.fillStyle = "#9CA3AF";
  ctx.textAlign = "right";
  ctx.fillText("my.endless-storage.com", WIDTH - PADDING, HEIGHT - 20);

  console.log("[Label Draw] Converting canvas to data URL...");
  const dataUrl = canvas.toDataURL("image/png");
  console.log(
    `[Label Draw] Label complete in ${(performance.now() - drawStart).toFixed(2)}ms, data URL length: ${dataUrl.length}`
  );

  return dataUrl;
}

/**
 * Generate labels and package into ZIP or PDF
 */
export async function generateLabelsPackage(
  labels: Array<{ code: string; labelDataUrl: string }>,
  exportFormat: "zip" | "pdf",
  filenamePrefix: string
): Promise<Blob> {
  if (exportFormat === "pdf") {
    // Dynamic import to avoid loading jspdf when not needed
    const { jsPDF } = await import("jspdf");

    // Avery 5163 layout (2 columns x 5 rows = 10 labels per sheet)
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "in",
      format: "letter",
    });

    const COLS = 2;
    const ROWS = 5;
    const LABELS_PER_PAGE = COLS * ROWS;
    const LABEL_WIDTH = 4;
    const LABEL_HEIGHT = 2;
    const MARGIN_LEFT = 0.15625;
    const MARGIN_TOP = 0.5;
    const GAP_X = 0.1875;

    for (let i = 0; i < labels.length; i++) {
      const positionOnPage = i % LABELS_PER_PAGE;
      const col = positionOnPage % COLS;
      const row = Math.floor(positionOnPage / COLS);

      if (i > 0 && positionOnPage === 0) {
        pdf.addPage();
      }

      const x = MARGIN_LEFT + col * (LABEL_WIDTH + GAP_X);
      const y = MARGIN_TOP + row * LABEL_HEIGHT;

      pdf.addImage(labels[i].labelDataUrl, "PNG", x, y, LABEL_WIDTH, LABEL_HEIGHT);
    }

    return pdf.output("blob");
  } else {
    // ZIP format
    const JSZip = (await import("jszip")).default;
    const zip = new JSZip();

    for (const { code, labelDataUrl } of labels) {
      const base64 = labelDataUrl.split(",")[1];
      zip.file(`${code}.png`, base64, { base64: true });
    }

    return await zip.generateAsync({ type: "blob" });
  }
}
